<script setup lang="ts">
/**
 * "กำลังจัดส่ง" under the POS row of the customer tab (owner 27 ก.ย. 69: "แถวนี้แสดงออเดอร์ยังส่งไม่เสร็จ เผื่อลูกค้า
 * มาถามเราได้มีข้อมูลแบบสดๆ ทั้ง lalamove และ dhl"). One card per order that is not delivered yet (+ the ones
 * delivered in the last 48 h, dimmed, last).
 * Detailed card (owner-approved mockup v4, 28 ก.ย.: "อยากได้รายละเอียดมากกว่านี้ถ้าอยู่ในช่วงกำลังส่ง" · "ไม่ต้องใส่
 * map" · DHL "เห็นอันล่าสุดพอ และกดดูได้ที่เหลือ"): Lalamove once the driver has the food — arrival time + round pill,
 * the trip's stops, a time under each step, the driver (photo, plate, position age, distance, "ไม่ขยับ"), "แผนที่สด";
 * delivered → the proof photo. DHL — status, the latest scan (the rest folded), tracking no., weight, "ไม่มีสแกนใหม่";
 * delivered → receiver, photo, signature. The kitchen phases keep the compact card.
 * Every card: a polite message for the customer built from the card; "ใส่ข้อความในช่องตอบ" puts it in this chat's
 * typing box (never sends). Proof photos cannot ride along automatically (staged files must be library pictures) —
 * the toast says so. Pictures are the carriers' https links, opened only here (staff), never re-served by us.
 * Data: stores/deliveries.ts (polls every 60 s while shown, pauses while the browser tab is hidden). Chats not linked
 * to POS (web chats: not logged in on the site and not linked) show nothing.
 * `bare` (owner 17 ต.ค. 69, lean panel): inside the "ปัญหา / ติดตาม" card — no frame of its own, and only while an
 * order is on its way (no loading / "nothing on its way" lines: the card around it is hidden then).
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import type { DeliveryOrder } from '../../api/deliveries'
import type { ConversationDetail } from '../../api/types'
import dhlLogo from '../../assets/carriers/dhl.svg'
import lalamoveLogo from '../../assets/carriers/lalamove.png'
import {
  carrierName,
  customerMessage,
  dayClock,
  detailLines,
  detailMeta,
  driverLine,
  heroOf,
  isDetailed,
  itemsText,
  messagePreview,
  payText,
  phaseText,
  pillOf,
  scanFold,
  stepViews,
  tripParts,
  updatedText,
  whenText,
} from '../../lib/delivery'
import { baht } from '../../lib/inbox'
import { relTime } from '../../lib/webchat'
import { useClock } from '../../stores/clock'
import { useDeliveries } from '../../stores/deliveries'
import { useInbox } from '../../stores/inbox'
import { useOrders } from '../../stores/order'
import { useWebchat } from '../../stores/webchat'
import Icon from '../ui/Icon.vue'
import MediaViewer from '../ui/MediaViewer.vue'

const SHOWN = 3
const RUNNING = ['finding_driver', 'to_pickup', 'on_the_way']

const props = defineProps<{ detail: ConversationDetail; bare?: boolean }>()
const store = useDeliveries()
const inbox = useInbox()
const clock = useClock()
const posOrders = useOrders()
const webchat = useWebchat()

const all = ref(false)
/** DHL cards whose earlier scans are open. */
const scansOpen = ref<Record<string, boolean>>({})
/** Pictures that did not load (expired carrier link): the icon / text stays. */
const broken = ref<Record<string, boolean>>({})
const viewer = ref<{ items: { type: string; url: string; name: string }[]; start: number } | null>(null)
const id = computed(() => props.detail.id)
watch(
  id,
  (cid) => {
    all.value = false
    scansOpen.value = {}
    store.watch(cid)
  },
  { immediate: true },
)
onBeforeUnmount(() => store.stop())

// linked just now (order tab / phone button) or the web visitor logged in on the site (signed identity): ask again for
// the new customer set (the server never uses a phone the browser sent — typed in the chat window or the website
// context's member: anyone can put anyone's number there)
const linkKey = computed(() => {
  const ctx = posOrders.contexts[id.value]
  const signed = props.detail.channel === 'web' ? !!webchat.visitors[id.value]?.identified : false
  return `${ctx?.customer_id ?? ''}|${signed}`
})
watch(linkKey, (k, old) => {
  if (old !== undefined && k !== old) void store.load(id.value)
})

const d = computed(() => store.data[id.value] ?? null)
const busy = computed(() => !!store.loading[id.value])
const err = computed(() => store.error[id.value] ?? null)
/** Linked to POS / a web phone as the panel knows it: worth a loading line before the first answer. */
const hint = computed(() => !!props.detail.pos || !!posOrders.contexts[id.value]?.customer_id)
const show = computed(() => {
  if (props.bare) return (d.value?.orders ?? []).some((o) => !o.dim)
  if (d.value) return d.value.state !== 'no_customer'
  return hint.value && (busy.value || !!err.value)
})
const list = computed(() => d.value?.orders ?? [])
const active = computed(() => list.value.filter((o) => !o.dim).length)
const cards = computed(() => (all.value ? list.value : list.value.slice(0, SHOWN)).map((o) => ({ o, v: view(o) })))
const fetched = computed(() => {
  const at = d.value?.fetched_at
  if (!at || d.value?.state !== 'ok') return null
  const rel = relTime(at, clock.now)
  return d.value.stale ? `ข้อมูลเมื่อ ${rel}` : null
})

interface Pod {
  photo: string | null
  sig: string | null
  title: string
  sub: string | null
}

/** Proof of delivery block (delivered only). */
function podOf(o: DeliveryOrder): Pod | null {
  if (o.phase !== 'delivered') return null
  const at = o.delivered_at ? dayClock(o.delivered_at, clock.now) : null
  if (o.carrier === 'lalamove' && o.lalamove?.pod_image) {
    return {
      photo: o.lalamove.pod_image,
      sig: null,
      title: 'รูปยืนยันการส่งจากคนขับ',
      sub: [at ? `ส่งถึง ${at}` : null, 'กดเพื่อดูรูปเต็ม'].filter(Boolean).join(' · '),
    }
  }
  const x = o.dhl
  if (o.carrier === 'dhl' && x && (x.delivery_image || x.epod_signature || x.epod_name)) {
    return {
      photo: x.delivery_image ?? null,
      sig: x.epod_signature ?? null,
      title: x.epod_name ? `รับโดย ${x.epod_name}` : 'ส่งถึงผู้รับแล้ว',
      sub: x.delivery_image ? 'รูปยืนยันการส่งจาก DHL · กดเพื่อดูรูปเต็ม' : x.epod_signature ? 'ลายเซ็นผู้รับจาก DHL' : null,
    }
  }
  return null
}

function view(o: DeliveryOrder) {
  const p = phaseText(o.carrier, o.phase)
  const detail = isDetailed(o)
  const msg = customerMessage(o, clock.now)
  const pill = detail ? pillOf(o) : null
  // mockup: the parcel with no new scan and the delivered parcel skip the step line (the warning / proof leads)
  const dhlNoSteps = o.carrier === 'dhl' && detail && (o.phase === 'delivered' || pill?.tone === 'warn')
  const running = RUNNING.includes(o.phase)
  return {
    p,
    detail,
    hero: detail ? heroOf(o, clock.now) : null,
    pill,
    trip: detail ? tripParts(o) : [],
    steps: (detail ? !dhlNoSteps : p.step !== null && !o.dim) ? stepViews(o, clock.now) : null,
    lines: detail ? [] : detailLines(o, clock.now),
    drv: running && o.lalamove?.driver ? driverLine(o.lalamove) : null,
    // mockup: the delivered parcel shows the proof, not the scans / tracking number
    fold: detail && o.dhl && o.phase !== 'delivered' ? scanFold(o.dhl, clock.now) : null,
    dhlDone: detail && o.carrier === 'dhl' && o.phase === 'delivered',
    pod: podOf(o),
    meta: detail
      ? detailMeta(o, clock.now)
      : [
          o.phase === 'scheduled' || o.phase === 'preparing' ? null : whenText(o, clock.now),
          itemsText(o),
          o.total !== null ? baht(o.total) : null,
          payText(o.payment),
        ]
          .filter(Boolean)
          .join(' · '),
    shipOut: o.carrier === 'dhl',
    updated: detail ? null : updatedText(o.updated_at, clock.now),
    msg,
    preview: msg ? messagePreview(msg) : null,
    map: running && o.lalamove?.share_link ? o.lalamove.share_link : null,
    copyLink: !!o.bill_url && o.phase !== 'delivered',
  }
}

async function copy(text: string, done: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    inbox.flash(done)
  } catch {
    inbox.flash('คัดลอกไม่ได้ ลองกดค้างที่ข้อความแล้วคัดลอกเอง')
  }
}

/** The card's message into this chat's typing box: what the staff typed stays above; an untouched AI draft goes. */
function putInReply(msg: string, pod: Pod | null): void {
  const st = inbox.composers[id.value]
  const keep = st && st.text.trim() && !(st.origin === 'ai' && !st.edited) ? st.text.trimEnd() : ''
  inbox.setText(id.value, '')
  inbox.setText(id.value, keep ? `${keep}\n\n${msg}` : msg)
  inbox.flash(
    pod?.photo || pod?.sig
      ? 'ใส่ข้อความในช่องตอบแล้ว · รูปยืนยันแนบให้อัตโนมัติไม่ได้ ถ้าจะส่งรูป บันทึกรูปแล้วแนบเอง'
      : 'ใส่ข้อความในช่องตอบแล้ว ตรวจแล้วกดส่ง',
  )
}

function openPod(pod: Pod, start: 'photo' | 'sig'): void {
  const items: { type: string; url: string; name: string }[] = []
  if (pod.photo) items.push({ type: 'image', url: pod.photo, name: 'รูปยืนยันการส่ง' })
  if (pod.sig) items.push({ type: 'image', url: pod.sig, name: 'ลายเซ็นผู้รับ' })
  if (!items.length) return
  viewer.value = { items, start: start === 'sig' && pod.photo && pod.sig ? 1 : 0 }
}
</script>

<template>
  <section v-if="show" class="cs dl" :class="{ bare }" aria-label="กำลังจัดส่ง">
    <span v-if="busy" class="dl-bar" role="progressbar" aria-label="กำลังโหลดสถานะขนส่ง"></span>
    <div class="sec-h">
      <Icon name="truck" />
      <span class="sec-t">กำลังจัดส่ง</span>
      <span v-if="active" class="dl-n" :aria-label="`${active} คำสั่งซื้อ`">{{ active }}</span>
      <span class="grow"></span>
      <span v-if="fetched" class="dl-stale" title="POS ตอบช้า จะอัปเดตเองเมื่อ POS ตอบ">{{ fetched }}</span>
      <button
        class="dl-ref"
        type="button"
        :disabled="busy"
        aria-label="โหลดสถานะขนส่งใหม่"
        title="โหลดใหม่"
        @click="store.refresh(id)"
      >
        <Icon name="refresh" />
      </button>
    </div>

    <p v-if="!d && busy" class="dl-empty">กำลังโหลดสถานะขนส่ง…</p>
    <p v-else-if="!d && err" class="dl-empty err">โหลดสถานะขนส่งไม่สำเร็จ: {{ err }}</p>
    <p v-else-if="d && d.state === 'pending'" class="dl-empty">กำลังถาม POS…</p>
    <p v-else-if="d && d.state === 'pos_error'" class="dl-empty err">{{ d.message || 'ต่อ POS ไม่ได้ ลองใหม่อีกครั้ง' }}</p>
    <p v-else-if="d && !list.length" class="dl-empty">ไม่มีคำสั่งซื้อที่กำลังจัดส่ง</p>

    <ul v-if="list.length" class="dl-list">
      <li v-for="{ o, v } in cards" :key="o.id" class="dl-card" :class="{ dim: o.dim, detail: v.detail }">
        <div class="dl-top">
          <img
            v-if="o.carrier !== 'other'"
            class="dl-logo"
            :class="o.carrier"
            :src="o.carrier === 'dhl' ? dhlLogo : lalamoveLogo"
            :alt="carrierName(o.carrier)"
          />
          <span v-else class="dl-other">{{ carrierName(o.carrier) }}</span>
          <span class="dl-code mono">{{ o.code }}</span>
          <span class="grow"></span>
          <span class="dl-chip" :class="`t-${v.p.tone}`"
            ><Icon v-if="o.phase === 'delivered'" name="checkCircle" />{{ v.p.chip }}</span
          >
        </div>

        <!-- detailed: arrival / status big + round pill (Lalamove) or "no new scan" (DHL) -->
        <div v-if="v.hero" class="dl-hero">
          <p class="dl-big">
            <b>{{ v.hero.big }}</b><span v-if="v.hero.sub" class="dl-hsub" :class="{ warn: v.hero.warn }">{{ v.hero.sub }}</span>
          </p>
          <span v-if="v.pill" class="dl-pill" :class="v.pill.tone"
            ><Icon :name="v.pill.tone === 'ok' ? 'checkCircle' : 'alert'" />{{ v.pill.text }}</span
          >
        </div>
        <p v-if="v.trip.length" class="dl-trip">
          <template v-for="(t, i) in v.trip" :key="i"
            ><span v-if="i" class="dl-sep" aria-hidden="true"> · </span><b v-if="t.strong">{{ t.text }}</b
            ><span v-else>{{ t.text }}</span></template
          >
        </p>

        <!-- compact: title + lines (kitchen, driver going to the shop, cancelled) -->
        <div v-if="!v.detail" class="dl-status">
          <p class="dl-title">{{ v.p.title }}</p>
          <p v-for="(ln, i) in v.lines" :key="i" class="dl-sub" :class="{ strong: ln.strong }">{{ ln.text }}</p>
          <p v-if="v.updated" class="dl-upd"><Icon name="clock" />{{ v.updated }}</p>
        </div>

        <div v-if="v.pod" class="dl-pod">
          <button
            v-if="v.pod.photo && !broken[`pod-${o.id}`]"
            type="button"
            class="dl-pod-ph"
            aria-label="ดูรูปยืนยันการส่งขนาดเต็ม"
            @click="openPod(v.pod, 'photo')"
          >
            <img
              :src="v.pod.photo"
              alt="รูปยืนยันการส่ง"
              loading="lazy"
              referrerpolicy="no-referrer"
              @error="broken[`pod-${o.id}`] = true"
            />
          </button>
          <span v-else class="dl-pod-ph none" aria-hidden="true"><Icon name="image" /></span>
          <span class="dl-pod-t">
            <b>{{ v.pod.title }}</b>
            <span v-if="v.pod.sub">{{ broken[`pod-${o.id}`] ? 'เปิดรูปไม่ได้ (ลิงก์ของขนส่งอาจหมดอายุ)' : v.pod.sub }}</span>
          </span>
          <button
            v-if="v.pod.sig && !broken[`sig-${o.id}`]"
            type="button"
            class="dl-sig"
            aria-label="ดูลายเซ็นผู้รับ"
            @click="openPod(v.pod, 'sig')"
          >
            <img :src="v.pod.sig" alt="ลายเซ็นผู้รับ" @error="broken[`sig-${o.id}`] = true" />
          </button>
        </div>

        <ol v-if="v.steps" class="dl-steps" :aria-label="`ขั้นตอน ${carrierName(o.carrier)}: ${v.p.title}`">
          <li
            v-for="s in v.steps"
            :key="s.label"
            :class="[s.state, { est: s.est }]"
            :aria-current="s.state === 'now' ? 'step' : undefined"
            :title="s.title ?? undefined"
          >
            <span class="dl-dot" aria-hidden="true"></span>
            <span class="dl-st">{{ s.label }}</span>
            <time v-if="s.time" class="dl-st-t">{{ s.time }}</time>
          </li>
        </ol>

        <div v-if="v.fold?.latest" class="dl-scans">
          <ul class="dl-scan" aria-label="สแกนของ DHL ล่าสุดก่อน">
            <li class="first">
              <span class="dl-sd" aria-hidden="true"></span>
              <span class="dl-sx"
                >{{ v.fold.latest.text }}<span v-if="v.fold.latest.place" class="dl-spl">{{ v.fold.latest.place }}</span></span
              >
              <time :title="v.fold.latest.title">{{ v.fold.latest.time }}</time>
            </li>
            <template v-if="scansOpen[o.id]">
              <li v-for="(x, i) in v.fold.rest" :key="i">
                <span class="dl-sd" aria-hidden="true"></span>
                <span class="dl-sx">{{ x.text }}<span v-if="x.place" class="dl-spl">{{ x.place }}</span></span>
                <time :title="x.title">{{ x.time }}</time>
              </li>
            </template>
          </ul>
          <button
            v-if="v.fold.rest.length"
            type="button"
            class="dl-fold"
            :aria-expanded="!!scansOpen[o.id]"
            @click="scansOpen[o.id] = !scansOpen[o.id]"
          >
            <Icon :name="scansOpen[o.id] ? 'chevronUp' : 'chevronDown'" />{{
              scansOpen[o.id] ? 'ซ่อนสแกนก่อนหน้า' : `ดูสแกนก่อนหน้าอีก ${v.fold.rest.length} รายการ`
            }}
          </button>
        </div>

        <div v-if="v.drv && o.lalamove?.driver" class="dl-drv">
          <span class="dl-drv-av" aria-hidden="true"
            ><img
              v-if="o.lalamove.driver.photo && !broken[`drv-${o.id}`]"
              :src="o.lalamove.driver.photo"
              alt=""
              loading="lazy"
              referrerpolicy="no-referrer"
              @error="broken[`drv-${o.id}`] = true" /><Icon v-else name="user"
          /></span>
          <span class="dl-drv-n">
            <b>{{ o.lalamove.driver.name || 'คนขับ Lalamove' }}</b>
            <span class="dl-drv-s"
              >{{ v.drv.parts.join(' · ')
              }}<template v-if="v.drv.warn"
                ><span v-if="v.drv.parts.length" aria-hidden="true"> · </span
                ><b class="warn">{{ v.drv.warn }}</b></template
              ></span
            >
          </span>
          <span v-if="o.lalamove.driver.phone || v.map" class="dl-drv-act">
            <a
              v-if="o.lalamove.driver.phone"
              class="dl-call"
              :href="`tel:${o.lalamove.driver.phone}`"
              :aria-label="`โทรหาคนขับ ${o.lalamove.driver.name ?? ''} ${o.lalamove.driver.phone_display ?? ''}`"
              ><Icon name="phone" /><span class="mono">{{ o.lalamove.driver.phone_display }}</span></a
            >
            <a
              v-if="v.map"
              class="dl-map"
              :href="v.map"
              target="_blank"
              rel="noopener noreferrer"
              title="เปิดแผนที่สดของ Lalamove ในแท็บใหม่"
              >แผนที่สด<Icon name="external"
            /></a>
          </span>
        </div>

        <div v-if="o.dhl?.tracking_no && !v.dhlDone" class="dl-trk">
          <span class="dl-trk-l">เลขพัสดุ</span>
          <span class="dl-trk-n mono">{{ o.dhl.tracking_no }}</span>
          <span class="grow"></span>
          <button
            type="button"
            class="dl-mini icon"
            title="คัดลอกเลขพัสดุ"
            :aria-label="`คัดลอกเลขพัสดุ ${o.dhl.tracking_no}`"
            @click="copy(o.dhl.tracking_no, 'คัดลอกเลขพัสดุแล้ว')"
          >
            <Icon name="copy" />
          </button>
          <a
            v-if="o.dhl.track_url"
            class="dl-mini"
            :href="o.dhl.track_url"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="เปิดหน้าติดตามพัสดุของ DHL"
            >หน้า DHL<Icon name="external"
          /></a>
        </div>

        <p
          v-if="v.meta"
          class="dl-meta"
          :title="v.shipOut ? 'วันส่งในบิล DHL = วันที่ DHL มารับพัสดุ ลูกค้าได้รับ 1–3 วันหลังจากนั้นตามพื้นที่' : undefined"
        >
          {{ v.meta }}
        </p>

        <!-- owner 28 ก.ย. 23:14: the two actions as small icons at the end of the message line (was a row of big buttons) -->
        <div v-if="v.preview || v.msg || (v.copyLink && o.bill_url)" class="dl-msgrow">
          <p v-if="v.preview" class="dl-msg" :title="v.msg ?? undefined">
            <span class="dl-msg-l">ข้อความแจ้งลูกค้า</span>“{{ v.preview }}”
          </p>
          <span v-else class="grow"></span>
          <button
            v-if="v.msg"
            type="button"
            class="dl-mini icon pri dl-put"
            :title="v.pod?.photo || v.pod?.sig ? 'ใส่ข้อความแจ้งลูกค้าในช่องตอบ (รูปยืนยันแนบให้อัตโนมัติไม่ได้)' : 'ใส่ข้อความแจ้งลูกค้าในช่องตอบ ตรวจแล้วค่อยกดส่ง'"
            aria-label="ใส่ข้อความแจ้งลูกค้าในช่องตอบ"
            @click="putInReply(v.msg, v.pod)"
          >
            <Icon name="reply" />
          </button>
          <button
            v-if="v.copyLink && o.bill_url"
            type="button"
            class="dl-mini icon dl-copy"
            title="คัดลอกลิงก์ติดตาม — หน้าบิลของลูกค้า แสดงสถานะจัดส่งล่าสุด"
            aria-label="คัดลอกลิงก์ติดตาม"
            @click="copy(o.bill_url, 'คัดลอกลิงก์ติดตามแล้ว วางส่งให้ลูกค้าได้เลย')"
          >
            <Icon name="link" />
          </button>
        </div>

        <div v-if="v.map && !v.drv" class="dl-foot">
          <span class="grow"></span>
          <a
            v-if="v.map && !v.drv"
            class="dl-btn ghost"
            :href="v.map"
            target="_blank"
            rel="noopener noreferrer"
            title="เปิดแผนที่สดของ Lalamove ในแท็บใหม่"
            >แผนที่สด<Icon name="external"
          /></a>
        </div>
      </li>
    </ul>
    <button v-if="list.length > SHOWN" class="dl-more" type="button" @click="all = !all">
      {{ all ? 'ย่อรายการ' : `ดูอีก ${list.length - SHOWN} คำสั่งซื้อ` }}
    </button>
    <p v-if="d?.more" class="dl-note">มีอีก {{ d.more }} คำสั่งซื้อ ดูทั้งหมดในแท็บประวัติ</p>
    <MediaViewer v-if="viewer" :items="viewer.items" :start="viewer.start" @close="viewer = null" />
  </section>
</template>

<style scoped>
.dl {
  position: relative;
}
.dl.bare {
  padding: 12px 14px;
  border-bottom: 0;
}
.dl .sec-h > :deep(.ic) {
  width: 16px;
  height: 16px;
  color: var(--mut);
}
.dl-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  overflow: hidden;
  background: var(--acs);
}
.dl-bar::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 30%;
  background: var(--ac);
  animation: dl-slide 1.1s ease-in-out infinite;
}
@keyframes dl-slide {
  from {
    left: -30%;
  }
  to {
    left: 100%;
  }
}
.dl-n {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}
.dl-stale {
  font-size: 12px;
  line-height: 18px;
  color: #7a4700;
}
.dl-ref {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
  flex: none;
}
.dl-ref:hover:not(:disabled) {
  background: #f1f5f3;
  color: var(--ink);
}
.dl-ref:disabled {
  opacity: 0.5;
}
.dl-ref :deep(.ic) {
  width: 15px;
  height: 15px;
}
.dl-empty {
  margin: 0;
  font-size: 12.5px;
  line-height: 20px;
  color: var(--mut);
}
.dl-empty.err {
  color: var(--crit);
}
.dl-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dl-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
}
.dl-card.dim {
  background: #fafbfa;
}
.dl-card.dim > * {
  opacity: 0.78;
}
.dl-top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
}
.dl-logo {
  display: block;
  flex: none;
  height: 15px;
  width: auto;
}
.dl-logo.dhl {
  height: 13px;
  border-radius: 2px;
}
.dl-other {
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--mut);
}
.dl-code {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.dl-chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.dl-chip :deep(.ic) {
  width: 13px;
  height: 13px;
}
.t-neutral {
  background: #eef2f0;
  color: #3e4d46;
}
.t-info {
  background: #e8f0fb;
  color: #1f4f8f;
}
.t-go {
  background: var(--acs);
  color: var(--ac2);
}
.t-done {
  background: var(--acs);
  color: var(--ac2);
}
.t-warn {
  background: var(--warns);
  color: #7a4700;
}
.t-crit {
  background: var(--crits);
  color: #a61b12;
}
.dl-status {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.dl-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
}
.dl-sub {
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.dl-sub.strong {
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
}
.dl-steps {
  list-style: none;
  margin: 2px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.dl-steps li {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.dl-steps li + li::before {
  content: '';
  position: absolute;
  top: 4px;
  right: 50%;
  width: 100%;
  height: 2px;
  background: #dfe6e2;
}
.dl-steps li.done::before,
.dl-steps li.now::before {
  background: var(--ac);
}
.dl-dot {
  position: relative;
  z-index: 1;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #cdd7d2;
}
.dl-steps li.done .dl-dot {
  background: var(--ac);
  border-color: var(--ac);
}
.dl-steps li.now .dl-dot {
  background: #fff;
  border-color: var(--ac);
  box-shadow: 0 0 0 3px #d5eee2;
}
.dl-st {
  max-width: 100%;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  text-align: center;
  overflow-wrap: anywhere;
}
.dl-steps li.now .dl-st {
  color: var(--ink);
  font-weight: 600;
}
.dl-st-t {
  margin-top: -2px;
  font-size: 12px;
  line-height: 18px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.dl-steps li.est .dl-st-t {
  color: var(--mut);
}
.dl-drv {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 10px;
  padding: 8px 8px 8px 9px;
  border-radius: 10px;
  background: #f6f8f7;
}
.dl-drv-av {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: var(--mut);
  border: 1px solid var(--line);
}
.dl-drv-av img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dl-drv-av :deep(.ic) {
  width: 16px;
  height: 16px;
}
.dl-drv-n {
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.dl-drv-n b {
  font-weight: 600;
}
.dl-drv-s {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dl-drv-s b.warn {
  font-weight: 600;
  color: var(--warn);
}
.dl-drv-act {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}
.dl-map {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: var(--ac2);
  text-decoration: none;
  white-space: nowrap;
}
.dl-map:hover {
  text-decoration: underline;
}
.dl-map :deep(.ic) {
  width: 12px;
  height: 12px;
}
.dl-call {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #cfe3d8;
  background: #fff;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  text-decoration: none;
  white-space: nowrap;
}
.dl-call:hover {
  background: var(--acs);
}
.dl-call :deep(.ic) {
  width: 14px;
  height: 14px;
}
.dl-call .mono {
  font-size: 12px;
}
.dl-trk {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  padding: 7px 8px 7px 10px;
  border-radius: 10px;
  background: #f6f8f7;
  font-size: 12.5px;
  line-height: 19px;
}
.dl-trk-l {
  color: var(--mut);
  font-size: 12px;
}
.dl-trk-n {
  font-size: 12.5px;
  letter-spacing: 0.2px;
}
.dl-mini {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  border-radius: 7px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-decoration: none;
  white-space: nowrap;
}
.dl-mini:hover {
  background: #f1f5f3;
}
.dl-mini :deep(.ic) {
  width: 12px;
  height: 12px;
}
.dl-mini.icon {
  width: 26px;
  padding: 0;
  justify-content: center;
}
.dl-mini.icon :deep(.ic) {
  width: 13px;
  height: 13px;
}
.dl-meta {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dl-upd {
  margin: 1px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dl-upd :deep(.ic) {
  width: 12px;
  height: 12px;
}
/* ---- detailed card */
.dl-hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}
.dl-big {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 10px;
  font-size: 13px;
  line-height: 20px;
}
.dl-big b {
  font-size: 19px;
  line-height: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.dl-hsub {
  color: var(--mut);
}
.dl-hsub.warn {
  color: var(--warn);
  font-weight: 600;
}
.dl-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
}
.dl-pill :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
}
.dl-pill.ok {
  background: var(--acs);
  color: var(--ac2);
}
.dl-pill.warn {
  background: var(--warns);
  color: var(--warn);
}
.dl-trip {
  margin: -2px 0 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.dl-trip b {
  font-weight: 600;
  color: var(--ink);
}
.dl-pod {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dl-pod-ph {
  flex: none;
  width: 84px;
  height: 64px;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--line);
  background: #f1f4f3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
  cursor: zoom-in;
}
.dl-pod-ph.none {
  cursor: default;
}
.dl-pod-ph img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dl-pod-ph :deep(.ic) {
  width: 20px;
  height: 20px;
}
.dl-pod-t {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.dl-pod-t b {
  font-weight: 600;
}
.dl-pod-t span {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dl-sig {
  flex: none;
  width: 72px;
  height: 38px;
  padding: 2px;
  border-radius: 6px;
  border: 1px dashed #c9d3ce;
  background: #fff;
  cursor: zoom-in;
}
.dl-sig img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.dl-scans {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.dl-scan {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.dl-scan li {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto;
  column-gap: 8px;
  align-items: start;
  padding: 4px 0;
  font-size: 13px;
  line-height: 19px;
}
.dl-scan li + li {
  border-top: 1px dashed var(--line2);
}
.dl-sd {
  justify-self: center;
  margin-top: 5px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #cdd7d2;
}
.dl-scan li.first .dl-sd {
  background: var(--ac);
  box-shadow: 0 0 0 3px #d5eee2;
}
.dl-scan li.first .dl-sx {
  font-weight: 600;
}
.dl-sx {
  min-width: 0;
  overflow-wrap: anywhere;
}
.dl-spl {
  display: block;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: var(--mut);
}
.dl-scan time {
  font-size: 12px;
  line-height: 19px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.dl-fold {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 0 0 22px;
  background: none;
  font-size: 12.5px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
}
.dl-fold :deep(.ic) {
  width: 14px;
  height: 14px;
}
.dl-msgrow {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.dl-msgrow .dl-msg {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 3px;
}
.dl-mini.pri {
  border-color: var(--ac);
  background: var(--ac);
  color: #fff;
}
.dl-mini.pri:hover {
  background: var(--ac2);
}
.dl-msg {
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  overflow-wrap: anywhere;
}
.dl-msg-l {
  margin-right: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ac2);
}
.dl-foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 9px;
  border-top: 1px solid var(--line2);
}
.dl-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 9px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  text-decoration: none;
  white-space: nowrap;
}
.dl-btn :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
}
.dl-btn.pri {
  background: var(--ac);
  border-color: var(--ac);
  color: #fff;
}
.dl-btn.pri:hover {
  background: var(--ac2);
}
.dl-btn.out {
  border-color: #9fcdb8;
  color: var(--ac2);
}
.dl-btn.out:hover,
.dl-btn.ghost:hover {
  background: var(--acs);
}
.dl-btn.ghost {
  padding: 0 8px;
  color: var(--mut);
  font-weight: 500;
}
.dl-btn.ghost :deep(.ic) {
  width: 12px;
  height: 12px;
}
.dl-more {
  align-self: flex-start;
  padding: 0;
  background: none;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  color: var(--ac2);
}
.dl-note {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
@media (prefers-reduced-motion: reduce) {
  .dl-bar::after {
    animation: none;
    left: 0;
    width: 100%;
    opacity: 0.4;
  }
}
</style>
